/**
 * NMS FAQ Page Component
 * Handles pill navigation and FAQ expansion functionality
 * Extends NMSBaseClass for shared functionality
 */
class NMSFaqPage extends window.NMSBaseClass {
  #modalEscHandler = null;
  #lockedScrollY = 0;
  #modalAbortController = null;

  constructor(element) {
    super();
    this.element = element;
    this.sectionId = element.dataset.sectionId;

    // Store in global registry
    window.neemans = window.neemans || {};
    window.neemans.faqPage = window.neemans.faqPage || {};
    window.neemans.faqPage[this.sectionId] = this;

    // Component state
    this.activeCategory = null;
  }

  init() {
    super.init();
    this.#setupInitialState();
    this.#bindScrollIndicators();
    this.#bindFAQEvents();
    this.#bindFilterButton();
  }

  /**
   * Handle pill click to switch FAQ categories
   * Event handler - auto-bound via data-nms-click="/handlePillClick/categoryId"
   */
  handlePillClick(event, categoryId) {
    event.preventDefault();

    const clickedPill = event.currentTarget;

    // Don't do anything if already active
    if (clickedPill.classList.contains('nms-pill--active')) {
      return;
    }

    this.#switchToCategory(categoryId, clickedPill);
  }


  /**
   * Bind FAQ accordion events for auto-close behavior
   */
  #bindFAQEvents() {
    // Bind events for all FAQ items (same structure as product FAQ)
    const allFAQs = this.element.querySelectorAll('[data-nms-js="faq-item"]');

    for (const faq of allFAQs) {
      const question = faq.querySelector('[data-nms-js="faq-question"]');

      if (question) {
        question.addEventListener('click', () => this.toggleFAQ(faq));
        question.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleFAQ(faq);
          }
        });
      }
    }
  }

  /**
   * Bind filter button events
   */
  #bindFilterButton() {
    const filterButton = this.element.querySelector('[data-ref="filterButton"]');

    console.log('Filter button found:', filterButton); // Debug log

    if (filterButton) {
      filterButton.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Filter button clicked'); // Debug log
        this.openFilterModal();
      });
    } else {
      console.error('Filter button not found');
    }
  }

  /**
   * Toggle FAQ item (same logic as product FAQ)
   */
  toggleFAQ(clickedItem) {
    const isOpen = clickedItem.classList.contains('nms-page-faq__item--open');

    // Toggle the clicked item first
    if (isOpen) {
      this.#closeFAQ(clickedItem);
    } else {
      // Close other FAQs in the same category before opening this one
      this.#closeOtherFAQsInCategory(clickedItem);
      this.#openFAQ(clickedItem);
    }
  }

  /**
   * Open FAQ item (same logic as product FAQ)
   */
  #openFAQ(item) {
    const answer = item.querySelector('[data-nms-js="faq-answer"]');
    const button = item.querySelector('[data-nms-js="faq-question"]');

    if (!answer || !button) return;

    item.classList.add('nms-page-faq__item--open');
    button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close FAQ item (same logic as product FAQ)
   */
  #closeFAQ(item) {
    const answer = item.querySelector('[data-nms-js="faq-answer"]');
    const button = item.querySelector('[data-nms-js="faq-question"]');

    if (!answer || !button) return;

    item.classList.remove('nms-page-faq__item--open');
    button.setAttribute('aria-expanded', 'false');
  }

  /**
   * Close other FAQs in the same category when one opens
   */
  #closeOtherFAQsInCategory(openFAQ) {
    const category = openFAQ.closest('.nms-faq-page__category');
    if (!category) return;

    const categoryFAQs = category.querySelectorAll('[data-nms-js="faq-item"]');

    for (const faq of categoryFAQs) {
      if (faq !== openFAQ && faq.classList.contains('nms-page-faq__item--open')) {
        this.#closeFAQ(faq);
      }
    }
  }

  /**
   * Set up initial state - show first category
   */
  #setupInitialState() {
    const firstPill = this.element.querySelector('[data-ref="pillButtons[]"]');
    const firstCategory = this.element.querySelector('.nms-faq-page__category:not(.nms-faq-page__category--hidden)');

    if (firstPill && firstCategory) {
      const categoryId = firstPill.dataset.nmsPillTarget;
      this.activeCategory = categoryId;

      // Set first pill as active
      firstPill.classList.add('nms-pill--active');
      firstPill.setAttribute('aria-selected', 'true');

      // Set first category as visible
      firstCategory.classList.remove('nms-faq-page__category--hidden');
      firstCategory.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Switch to a specific FAQ category
   */
  #switchToCategory(categoryId, clickedPill) {
    // Update pills
    this.#updatePillStates(clickedPill);

    // Update category content
    this.#updateCategoryStates(categoryId);

    // Update component state
    this.activeCategory = categoryId;

    // Dispatch custom event
    this.dispatchNMSEvent('category-changed', {
      previousCategory: this.activeCategory,
      newCategory: categoryId,
      pillElement: clickedPill
    });
  }

  /**
   * Update pill button states
   */
  #updatePillStates(activePill) {
    const allPills = this.element.querySelectorAll('[data-ref="pillButtons[]"]');

    for (const pill of allPills) {
      if (pill === activePill) {
        pill.classList.add('nms-pill--active');
        pill.setAttribute('aria-selected', 'true');
      } else {
        pill.classList.remove('nms-pill--active');
        pill.setAttribute('aria-selected', 'false');
      }
    }
  }

  /**
   * Update FAQ category visibility states
   */
  #updateCategoryStates(activeCategoryId) {
    const allCategories = this.element.querySelectorAll('.nms-faq-page__category');

    for (const category of allCategories) {
      if (category.id === `${activeCategoryId}-content`) {
        category.classList.remove('nms-faq-page__category--hidden');
        category.setAttribute('aria-hidden', 'false');
      } else {
        category.classList.add('nms-faq-page__category--hidden');
        category.setAttribute('aria-hidden', 'true');
      }
    }
  }


  /**
   * Bind scroll indicators for horizontal pill navigation
   */
  #bindScrollIndicators() {
    const pillsContainer = this.element.querySelector('.nms-pills-navigation__container');
    const pillsNavEl = this.element.querySelector('.nms-pills-navigation__nav');
    const pillsRoot = this.element.querySelector('.nms-pills-navigation');

    if (!pillsContainer || !pillsNavEl || !pillsRoot) return;

    // Get scroll button references
    const scrollLeftButton = pillsRoot.querySelector('.nms-pills-navigation__scroll-button--left');
    const scrollRightButton = pillsRoot.querySelector('.nms-pills-navigation__scroll-button--right');

    const updateScrollIndicators = () => {
      const { scrollLeft, scrollWidth, clientWidth } = pillsContainer;

      // Check if content is scrollable
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
      const hasOverflow = scrollWidth > clientWidth;

      // Update gradient visibility on nav element
      pillsNavEl.dataset.scrollLeft = canScrollLeft;
      pillsNavEl.dataset.scrollRight = canScrollRight;

      // Update button disabled states
      if (scrollLeftButton) {
        scrollLeftButton.disabled = !canScrollLeft;
      }
      if (scrollRightButton) {
        scrollRightButton.disabled = !canScrollRight;
      }

      // Add/remove overflow class on ROOT to control button visibility on desktop
      if (hasOverflow) {
        pillsRoot.classList.add('nms-pills-navigation--overflow');
        pillsRoot.classList.add('nms-pills-navigation--scrollable');
      } else {
        pillsRoot.classList.remove('nms-pills-navigation--overflow');
        pillsRoot.classList.remove('nms-pills-navigation--scrollable');
      }
    };

    // Debounced scroll handler
    const debouncedUpdate = this.debounce(updateScrollIndicators, 100);

    // Add scroll button functionality
    if (scrollLeftButton) {
      scrollLeftButton.addEventListener('click', () => {
        pillsContainer.scrollBy({ left: -200, behavior: 'smooth' });
      });
    }

    if (scrollRightButton) {
      scrollRightButton.addEventListener('click', () => {
        pillsContainer.scrollBy({ left: 200, behavior: 'smooth' });
      });
    }

    pillsContainer.addEventListener('scroll', debouncedUpdate);
    window.addEventListener('resize', debouncedUpdate);

    // Initial check
    updateScrollIndicators();
  }


  /**
   * Open filter modal
   * Event handler - auto-bound via data-nms-click="/openFilterModal"
   */
  openFilterModal() {
    console.log('openFilterModal called'); // Debug log
    console.log('Section ID:', this.sectionId); // Debug log
    const modalId = `filter-modal-faq-pills-${this.sectionId}`;
    const modal = document.getElementById(modalId);

    console.log('Looking for modal:', modalId, modal); // Debug log

    // Debug: List all modals in the DOM
    const allModals = document.querySelectorAll('.nms-filter-modal');
    console.log('All filter modals in DOM:', allModals);
    for (const m of allModals) {
      console.log('Modal ID:', m.id);
    }

    if (modal) {
      modal.style.display = 'flex';
      this.#lockScroll();

      // Set current active pill as checked
      this.#updateModalSelection();

      // Bind modal events
      this.#bindModalEvents();
    } else {
      console.error('Filter modal not found:', modalId);
      // Try to find any filter modal as fallback
      const fallbackModal = document.querySelector('.nms-filter-modal');
      console.log('Fallback modal found:', fallbackModal);

      if (fallbackModal) {
        console.log('Using fallback modal');
        fallbackModal.style.display = 'flex';
        this.#lockScroll();

        // Set current active pill as checked
        this.#updateModalSelection();

        // Bind modal events
        this.#bindModalEvents();
      }
    }
  }

  /**
   * Close filter modal
   */
  closeFilterModal() {
    const modalId = `filter-modal-faq-pills-${this.sectionId}`;
    let modal = document.getElementById(modalId);

    // If the targeted modal isn't found, close any open filter modal
    if (!modal) {
      modal = document.querySelector('.nms-filter-modal[style*="display: flex"]')
        || document.querySelector('.nms-filter-modal');
    }

    if (modal) {
      this.#closeSpecificModal(modal);
    }
  }

  /**
   * Update modal selection to match current active pill
   */
  #updateModalSelection() {
    const modalId = `filter-modal-faq-pills-${this.sectionId}`;
    let modal = document.getElementById(modalId);

    // Fallback to any filter modal if specific one not found
    if (!modal) {
      modal = document.querySelector('.nms-filter-modal');
    }

    if (modal && this.activeCategory) {
      const radioInput = modal.querySelector(`input[value="${this.activeCategory}"]`);
      if (radioInput) {
        radioInput.checked = true;
      }
    }
  }

  /**
   * Bind modal events
   * Uses AbortController to prevent duplicate listeners on repeated opens
   */
  #bindModalEvents() {
    // Abort any previous modal listeners before adding new ones
    if (this.#modalAbortController) {
      this.#modalAbortController.abort();
    }
    this.#modalAbortController = new AbortController();
    const { signal } = this.#modalAbortController;

    const modalId = `filter-modal-faq-pills-${this.sectionId}`;
    let modal = document.getElementById(modalId);

    // Fallback to any filter modal if specific one not found
    if (!modal) {
      modal = document.querySelector('.nms-filter-modal');
    }

    if (!modal) return;

    // Close button
    const closeButton = modal.querySelector('.nms-filter-modal__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.#closeSpecificModal(modal), { signal });
    }

    // Backdrop click
    const backdrop = modal.querySelector('.nms-filter-modal__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          this.#closeSpecificModal(modal);
        }
      }, { signal });
    }

    // Radio button changes
    const radioInputs = modal.querySelectorAll('.nms-filter-modal__radio');
    for (const radio of radioInputs) {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const categoryId = e.target.value;
          this.switchToCategory(categoryId);
          // Ensure the corresponding pill is visible in the scroll container
          this.#scrollPillIntoView(categoryId);
          this.#closeSpecificModal(modal);
        }
      }, { signal });
    }

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeSpecificModal(modal);
      }
    }, { signal });
  }

  /**
   * Scroll the horizontal pills container so the target pill is visible
   */
  #scrollPillIntoView(categoryId) {
    const container = this.element.querySelector('.nms-pills-navigation__container');
    if (!container) return;

    const pill = this.element.querySelector(`[data-ref="pillButtons[]"][data-nms-pill-target="${categoryId}"]`);
    if (!pill) return;

    const pillRect = pill.getBoundingClientRect();
    const contRect = container.getBoundingClientRect();

    const fullyVisible = pillRect.left >= contRect.left && pillRect.right <= contRect.right;
    if (fullyVisible) return;

    // Target left so pill is centered within container
    const desiredLeft = pill.offsetLeft - (container.clientWidth - pill.clientWidth) / 2;
    const maxLeft = container.scrollWidth - container.clientWidth;
    const clamped = Math.max(0, Math.min(desiredLeft, maxLeft));

    container.scrollTo({ left: clamped, behavior: 'smooth' });
  }

  /**
   * Close a specific modal element and unlock page scroll
   */
  #closeSpecificModal(modal) {
    if (!modal) return;
    modal.style.display = 'none';
    this.#unlockScroll();

    // Abort all modal event listeners to prevent stale references
    if (this.#modalAbortController) {
      this.#modalAbortController.abort();
      this.#modalAbortController = null;
    }
  }

  /**
   * Prevent page from scrolling when modal is open (no scroll bleed)
   */
  #lockScroll() {
    // If already locked, skip
    if (document.body.classList.contains('nms-scroll-locked')) return;

    this.#lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('nms-scroll-locked');
    document.documentElement.classList.add('nms-modal-open');
  }

  /**
   * Restore page scroll to previous position
   */
  #unlockScroll() {
    const previousY = this.#lockedScrollY || 0;
    document.body.style.overflow = '';
    document.body.classList.remove('nms-scroll-locked');
    document.documentElement.classList.remove('nms-modal-open');
    if (previousY) {
      window.scrollTo(0, previousY);
    }
  }

  /**
   * Get category data for analytics or external access
   */
  getCategoryData() {
    return {
      activeCategory: this.activeCategory,
      sectionId: this.sectionId
    };
  }

  /**
   * Programmatically switch to a category (for external control)
   */
  switchToCategory(categoryId) {
    const targetPill = this.element.querySelector(`[data-nms-pill-target="${categoryId}"]`);

    if (targetPill) {
      this.#switchToCategory(categoryId, targetPill);
    } else {
      console.warn(`FAQ category "${categoryId}" not found`);
    }
  }

  /**
   * Search FAQs across all categories
   */
  searchFAQs(searchTerm) {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const results = [];

    const allCategories = this.element.querySelectorAll('.nms-faq-page__category');

    for (const category of allCategories) {
      const categoryId = category.dataset.faqCategory;
      const faqItems = category.querySelectorAll('[data-ref="faqItems[]"]');

      for (const faqItem of faqItems) {
        const question = faqItem.querySelector('.nms-product-faq__question-text');
        const answer = faqItem.querySelector('.nms-product-faq__answer-content');

        if (question && answer) {
          const questionText = question.textContent.toLowerCase();
          const answerText = answer.textContent.toLowerCase();

          if (questionText.includes(lowerSearchTerm) || answerText.includes(lowerSearchTerm)) {
            results.push({
              categoryId,
              element: faqItem,
              question: question.textContent.trim(),
              relevance: questionText.includes(lowerSearchTerm) ? 'high' : 'medium'
            });
          }
        }
      }
    }

    return results.sort((a, b) => {
      if (a.relevance === 'high' && b.relevance !== 'high') return -1;
      if (b.relevance === 'high' && a.relevance !== 'high') return 1;
      return 0;
    });
  }

  /**
   * Cleanup when component is destroyed
   */
  destroy() {
    super.destroy();

    // Clean up global reference
    if (window.neemans.faqPage?.[this.sectionId]) {
      delete window.neemans.faqPage[this.sectionId];
    }

    // Remove event listeners
    const pillsContainer = this.element.querySelector('.nms-pills-navigation__container');
    if (pillsContainer) {
      pillsContainer.removeEventListener('scroll', this.debouncedUpdate);
      window.removeEventListener('resize', this.debouncedUpdate);
    }

    // Abort all modal event listeners
    if (this.#modalAbortController) {
      this.#modalAbortController.abort();
      this.#modalAbortController = null;
    }
  }
}

/**
 * Initialize NMS FAQ Page components
 */
function initNMSFaqPage() {
  if (!window.NMSBaseClass) {
    console.error('NMSBaseClass is required for NMS FAQ Page component');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-faq-page]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSFaqPage(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSFaqPage);
} else {
  initNMSFaqPage();
}

// Re-initialize when new sections are added (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-nms-faq-page]')) {
    initNMSFaqPage();
  }
});

// Make available globally
window.NMSFaqPage = NMSFaqPage;
